import React, { useState, useEffect, useRef } from 'react';
import { Alert, Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import ListDetailsPretty from './ListDetailsPretty';
import { GatewayApi } from '../../../../api';
import { sleep } from '../../../../utils';
import NestedListDetailsPretty from './NestedListDetailsPretty';

// A replay is queued, not awaited: the gateway's outbound call inherits the operation's timeout, which
// is long enough that holding the request open isn't viable. We poll the new exchange for its response
// instead, and give up after the cap rather than spinning — an operation disabled between the click and
// the gateway dequeuing it leaves the exchange with no response at all.
const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 45;

const unEscapeJson = (string) => {
  if (string == null) {
    return '';
  }

  let parsedJSon;

  // escape added from Java REST response to avoid invalid JSON response
  // first try to parse to un-escape
  try {
    parsedJSon = JSON.parse(string);
  } catch (error) {
    return string ?? '';
  }

  // parse twice to make sure the final JSON object valid
  try {
    return JSON.parse(parsedJSon);
  } catch (error) {
    return parsedJSon;
  }
};

const reFormatLog = (log) => {
  const path = log.messages.path;
  const parameters = unEscapeJson(log.messages.parameters);
  const body = unEscapeJson(log.messages.body);
  const requestContent = unEscapeJson(log.messages.request);
  const receivedContent = unEscapeJson(log.messages.received);
  const responseContent = unEscapeJson(log.messages.response);
  return {
    ...log,
    messages: {
      request: requestContent,
      path: path,
      parameters: parameters,
      body: body,
      received: receivedContent,
      response: responseContent,
    },
  };
};

/**
 * Seed text for the resend editor, taken from the detail response's `requestContent` — the body exactly
 * as stored. `messages.request` is not usable here: the server collapses its tabs and newlines to spaces
 * for display, so an edit derived from it would reach the provider with significant line breaks already
 * destroyed. JSON is pretty-printed to make it editable, which is safe because it is semantically
 * identical and an unedited payload is never sent as a body at all.
 */
const toEditableText = (content) => {
  if (content == null) {
    return '';
  }
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch (error) {
    return content;
  }
};

const toDisplayText = (content) => {
  if (content == null) {
    return '';
  }
  return typeof content === 'string' ? content : JSON.stringify(content, null, 2);
};

// Only a finished, failed exchange may be replayed, matching the endpoint's own guard exactly. No status
// code at all means the exchange is still in flight — replaying one of those risks the provider
// receiving the same request twice — and anything outside 2xx counts as failed, not just 4xx and 5xx.
// messages.path is "<METHOD> <url>", so the verb is its first token. Anything that isn't a known read is
// treated as mutating — including the "Not available" placeholder for an orphaned operation — so an
// unrecognised method gets the stronger warning rather than the milder one.
const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS'];
const HTTP_METHODS = [...SAFE_METHODS, 'POST', 'PUT', 'PATCH', 'DELETE'];

// Null when the first token isn't a verb at all — the path reads "Not available (operation is null)" for
// an exchange whose operation has since been deleted, and naming that as the method would be nonsense.
const requestMethod = (log) => {
  const token = ((log.messages.path ?? '').trim().split(/\s+/)[0] || '').toUpperCase();
  return HTTP_METHODS.includes(token) ? token : null;
};

const isMutating = (log) => !SAFE_METHODS.includes(requestMethod(log));

const isSuccessStatus = (raw) => {
  const statusCode = Number(raw);
  return raw != null && raw !== '' && Number.isFinite(statusCode) && statusCode >= 200 && statusCode < 300;
};

const isResendable = (log) => {
  const raw = log.responseStatusCode;
  if (raw == null || raw === '') {
    return false;
  }
  // An exchange whose operation has since been deleted can't be replayed — the endpoint refuses it with a
  // conflict — so don't offer the button and make the operator discover that by clicking. The DTO omits
  // the short name when the operation is gone.
  if (!log.operationShortName) {
    return false;
  }
  return Number.isFinite(Number(raw)) && !isSuccessStatus(raw);
};

function ListDetails({ expand = false, logId }) {
  const [thisLog, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [payload, setPayload] = useState('');
  const [resend, setResend] = useState(null);
  const pollCancelled = useRef(false);
  // Which exchange this row is currently showing. A poll started for one exchange can wake after
  // pagination has re-pointed the row at another (the parent keys rows by index, so the same instance is
  // reused), and writing its outcome then would label the new exchange with the old one's result and
  // disable Resend for a row that was never replayed.
  const shownLogId = useRef(logId);

  useEffect(() => {
    if (!expand) return undefined;
    let cancelled = false;
    // The row is keyed by index in the parent, so this instance is reused across a change of logId and
    // across collapse/expand. The fetch state has to be cleared on every expand, or a row that once
    // failed to load stays stuck on that error and a row left expanded across pagination renders the
    // previous page's exchange. The resend state deliberately is not cleared here — see below.
    setLoading(true);
    setError(null);
    setLog(null);
    const fetchLog = async () => {
      try {
        const res = await GatewayApi.getStoreIntegrationExchangeLogs(logId);
        if (res.status != 200) {
          throw new Error('Failed to load exchange details...');
        }
        if (!cancelled) {
          setLog(reFormatLog(res.data));
        }
      } catch (error) {
        if (!cancelled) {
          setError(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchLog();
    return () => {
      cancelled = true;
      // Stop any resend poll still running against the exchange this row used to show, so it doesn't
      // keep hitting the API for a row nobody is looking at.
      pollCancelled.current = true;
    };
  }, [expand, logId]);

  // Keyed on logId alone, not on expand: the resend outcome belongs to the exchange, not to this
  // viewing of it. Clearing it on a mere collapse would re-enable Resend for an exchange that has
  // already been replayed, so collapsing and re-expanding a row would let it be sent twice.
  useEffect(() => {
    shownLogId.current = logId;
    setResend(null);
    // The dialog and its payload belong to the exchange it was opened for. Left alone they outlive a
    // change of logId: the loading branch unmounts the Modal, then modalOpen is still true when the new
    // detail lands so it reopens — header and warning describing the new exchange, textarea still holding
    // the old one's edited body. seededPayload now comes from the new exchange, so that stale text counts
    // as an edit and Resend would post it against the wrong exchange.
    setModalOpen(false);
    setPayload('');
  }, [logId]);

  // Every hook above runs on every render: returning before them would change the hook count between
  // the expanded and collapsed renders of the same row.
  if (!expand) return null;

  if (loading) {
    return (
      <>
        <tr>
          <td colSpan={7}>
            <div>Loading...</div>
          </td>
        </tr>
      </>
    );
  }

  if (error) {
    return (
      <>
        <tr>
          <td colSpan={7}>
            <div>Error: {error.message}</div>
          </td>
        </tr>
      </>
    );
  }

  // Absent when the payload was redacted as sensitive, or is binary. Without it there is nothing
  // faithful to edit, so only an unedited replay of the stored bytes is offered.
  const editable = thisLog.requestContent != null;
  const seededPayload = toEditableText(thisLog.requestContent);

  const openResend = () => {
    setPayload(seededPayload);
    setResend(null);
    setModalOpen(true);
  };

  const awaitResponse = async (exchangeId) => {
    // Only write state while the row still shows the exchange this poll belongs to.
    const stillOurs = () => shownLogId.current === logId;
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
      await sleep(POLL_INTERVAL_MS);
      // stillOurs() is checked for the exit as well as the write: pollCancelled is one ref shared by every
      // poll this row starts, and a later send resets it, which would otherwise un-cancel this loop and
      // leave it polling an exchange the row no longer shows for the rest of its attempts.
      if (pollCancelled.current || !stillOurs()) {
        // Stopped watching, but the replay is still queued — say so rather than leaving the row claiming
        // to be polling when nothing is. Unless the row has moved on, in which case this result is not
        // about what it is now showing.
        if (stillOurs()) {
          setResend({
            state: 'pending',
            exchangeId,
            message: `Stopped watching exchange ${exchangeId}. It was queued — refresh the log list to check on it.`,
          });
        }
        return;
      }
      try {
        const res = await GatewayApi.getStoreIntegrationExchangeLogs(exchangeId);
        const { messages, responseStatusCode } = res.data ?? {};
        // A status code is the reliable terminal signal: the DTO omits empty fields, so an exchange that
        // finished without a response body would never satisfy a messages.response check and the poll
        // would burn the full window on a replay that had already completed.
        if (messages?.response != null || (responseStatusCode != null && responseStatusCode !== '')) {
          if (stillOurs()) {
            setResend({
              state: 'complete',
              exchangeId,
              statusCode: responseStatusCode,
              response: toDisplayText(unEscapeJson(messages?.response)),
            });
          }
          return;
        }
      } catch (pollError) {
        // A poll that fails says nothing about the replay, which the gateway has already accepted. Keep
        // polling rather than surfacing this: calling it a failure would invite a second click, and the
        // provider would receive the request twice.
      }
    }
    if (stillOurs()) {
      setResend({
        state: 'pending',
        exchangeId,
        message: `Still running after ${(POLL_INTERVAL_MS * POLL_ATTEMPTS) / 1000}s. Exchange ${exchangeId} was created — refresh the log list to check on it.`,
      });
    }
  };

  const submitResend = async () => {
    setResend({ state: 'sending' });
    pollCancelled.current = false;
    let res;
    try {
      // Unedited means send no payload at all, so the gateway replays the stored request byte for byte
      // rather than whatever survived the round trip through this screen.
      const edited = editable && payload !== seededPayload;
      res = await GatewayApi.resendExchange(logId, edited ? payload : null, thisLog.requestContentType);
    } catch (err) {
      setResend({ state: 'error', message: err.response?.data?.message ?? err.message });
      return;
    }
    // The gateway has accepted the replay. From here nothing may report a failure or re-enable Resend —
    // the send cannot be taken back, and a second click would duplicate it at the provider. That is why
    // reading the id sits outside the try above: a response without one would otherwise throw into the
    // catch and render an accepted replay as a failure.
    // The dialog stays open so the outcome lands where there is room to read it. Cancel is live from here
    // (only `sending` disables it), so this doesn't lock the operator out while the replay runs, and
    // Resend is disabled from here so the open dialog can't queue a second one.
    const exchangeId = res.data?.id;
    if (exchangeId == null) {
      setResend({
        state: 'pending',
        message: 'Replay accepted, but no exchange id came back to follow — refresh the log list to check on it.',
      });
      return;
    }
    setResend({ state: 'polling', exchangeId });
    await awaitResponse(exchangeId);
  };

  const sending = resend?.state === 'sending';
  // Once the gateway has accepted a replay it cannot be recalled, so don't offer a second one for the
  // same row. A failed POST is different — nothing was sent, so that stays retryable.
  const accepted = ['polling', 'complete', 'pending'].includes(resend?.state);
  // A cleared payload is ambiguous — the endpoint rejects it rather than guess between "send nothing"
  // and "replay the stored request" — so say so here instead of after a round trip. Only when the seed
  // had content to begin with: a stored request that was itself blank would otherwise open the dialog
  // already disabled, with the message telling the operator to restore something that never existed.
  const payloadCleared = editable && seededPayload.trim() !== '' && payload.trim() === '';

  // Rendered in the modal, where there is room to read it, and against the row so the outcome survives
  // closing the modal. The response is one long line — the log DTO collapses newlines for display — so it
  // has to wrap rather than only scroll, or everything past the first line's width is invisible.
  const outcome = (
    <>
      {resend?.state === 'polling' && (
        <Alert color="info" className="mt-2 mb-0">
          Queued as exchange {resend.exchangeId}. Waiting for the response...
        </Alert>
      )}
      {resend?.state === 'complete' && (
        // Green only for a 2xx, matching isResendable's view of what counts as failed — keying off >= 400
        // would show a sub-200 status as a success.
        <Alert color={isSuccessStatus(resend.statusCode) ? 'success' : 'danger'} className="mt-2 mb-0">
          <div>
            Resent as exchange {resend.exchangeId} — status {resend.statusCode || 'none'}
          </div>
          <pre
            className="mt-2 mb-0"
            style={{ maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {resend.response}
          </pre>
        </Alert>
      )}
      {resend?.state === 'pending' && (
        <Alert color="warning" className="mt-2 mb-0">
          {resend.message}
        </Alert>
      )}
    </>
  );

  return (
    <>
      <tr>
        <td colSpan={7}>
          <Button
            color="primary"
            className="btn-sm"
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(thisLog, null, 2)).then(() => {
                alert('Copied JSON log to clipboard!');
              });
            }}
          >
            Copy Log to Clipboard
          </Button>
          {isResendable(thisLog) && (
            <Button color="warning" className="btn-sm ms-2" onClick={openResend} disabled={accepted}>
              <i className="fa-solid fa-rotate-right me-1"></i>
              Resend
            </Button>
          )}
          {thisLog.replayOfExchangeId && (
            <Badge color="info" className="ms-2">
              Replay of exchange {thisLog.replayOfExchangeId}
            </Badge>
          )}
          {!modalOpen && outcome}
          {resend?.state === 'error' && !modalOpen && (
            <Alert color="danger" className="mt-2 mb-0">
              Resend failed: {resend.message}
            </Alert>
          )}
        </td>
      </tr>
      <ListDetailsPretty title={'Request'} data={thisLog.messages.request} />
      <NestedListDetailsPretty
        mainTitle={'Sent'}
        data={{
          Path: thisLog.messages.path,
          Parameter: thisLog.messages.parameters,
          Body: thisLog.messages.body,
        }}
      />
      <ListDetailsPretty title={'Received'} data={thisLog.messages.received} />
      <ListDetailsPretty title={'Response'} data={thisLog.messages.response} />

      <Modal isOpen={modalOpen} size="lg" toggle={() => !sending && setModalOpen(false)}>
        <ModalHeader style={{ background: '#29363D' }}>
          <span style={{ color: '#ffffff' }}>
            <i className="fa-solid fa-rotate-right"></i> Resend {thisLog.operationShortName}
          </span>
        </ModalHeader>
        <ModalBody>
          <Alert color={isMutating(thisLog) ? 'danger' : 'warning'}>
            <div>
              {requestMethod(thisLog) ? (
                <>
                  <strong>{requestMethod(thisLog)}</strong> to{' '}
                </>
              ) : (
                'A call to '
              )}
              <b>{thisLog.integrationShortName}</b> —{' '}
              {isMutating(thisLog)
                ? `this repeats an operation that changes something at the provider. A failed exchange does
                   not always mean nothing happened: if the original was accepted and only its response was
                   lost, this does it a second time.`
                : `this repeats a read, so it should not change anything at the provider.`}
            </div>
            <div className="mt-2">
              It replays the <b>gateway call only</b>. Whatever the platform normally does once the call
              succeeds — recording a payment capture, advancing an order — does not run here, so a
              successful replay leaves our own records untouched.
            </div>
          </Alert>
          {editable ? (
            <>
              <label className="form-label" htmlFor="resendPayload">
                <strong>Gateway request</strong> — the payload as it arrived at the gateway, before the
                request transform. This is not the <em>Sent</em> body shown below, which cannot be
                replayed without bypassing the transform. Leave it untouched to resend it exactly as
                stored.
              </label>
              <textarea
                id="resendPayload"
                className="form-control"
                style={{ height: '320px', font: '13px/18px monospace' }}
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                disabled={sending}
              />
            </>
          ) : (
            <Alert color="secondary" className="mb-0">
              This payload can&apos;t be shown for editing — it is either redacted as a sensitive
              operation or not text. The stored request will be resent exactly as it was.
            </Alert>
          )}
          {payloadCleared && (
            <Alert color="warning" className="mt-2 mb-0">
              The payload is empty. Restore it, or cancel and resend without editing to replay the stored
              request unchanged.
            </Alert>
          )}
          {resend?.state === 'error' && (
            <Alert color="danger" className="mt-2 mb-0">
              Resend failed: {resend.message}
            </Alert>
          )}
          {outcome}
        </ModalBody>
        <ModalFooter>
          {/* Disabled once accepted as well as while sending: the dialog now stays open to show the
              outcome, so without this a second click would queue the same replay twice. */}
          <Button color="primary" onClick={submitResend} disabled={sending || accepted || payloadCleared}>
            {sending ? 'Sending...' : 'Resend'}
          </Button>
          <Button color="secondary" onClick={() => setModalOpen(false)} disabled={sending}>
            {accepted ? 'Close' : 'Cancel'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ListDetails;
